/**
 * rd-expiry W2.3 — Frame-1 pre-warn banner (presentational).
 *
 * A slim, NON-blocking top banner shown ~2 min before the reservation carrier
 * lapses, while the user STILL OWNS the reservation. "Keep" extends +15m
 * (guaranteed, via the W2.0 extend-hold rail). The form stays fully usable
 * underneath. CSS + SVG + copy are copied verbatim from the confirmed mockup
 * (session-recovery-states.html, Frame 1); copy says "reservation," never "hold".
 */

import styles from './PreWarnBanner.module.css';

export interface PreWarnBannerProps {
  /** "Keep" → extend the still-alive reservation (+15m via extend-hold). */
  onKeep: () => void;
  /** True while the extend request is in flight — disables "Keep". */
  keeping: boolean;
  /**
   * A transient extend failure (network / 429 / 5xx) — the reservation is still
   * alive, so the banner stays up with a retryable hint rather than evicting to
   * catch-after (MF2). Tapping "Keep" again retries.
   */
  errored?: boolean;
  /** Over its container at the container's width instead of pinned over the window (the website's desktop checkout). */
  inline?: boolean;
}

export function PreWarnBanner({ onKeep, keeping, errored = false, inline = false }: PreWarnBannerProps) {
  return (
    <div className={`${styles.prewarn} ${inline ? styles.inline : ''}`} role="status" data-testid="prewarn-banner">
      <span className={styles.pwIc} aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="13" r="8" />
          <path d="M12 9.5v3.5l2.5 1.5" />
          <path d="M9 2.5h6" />
        </svg>
      </span>
      <span className={styles.pwTx}>
        <b>2 min left on your reservation</b>
        <span>{errored ? "Couldn't reach the server. Tap Keep to try again" : 'Keep it. 15 more minutes to finish'}</span>
      </span>
      <button type="button" className={styles.pwKeep} onClick={onKeep} disabled={keeping}>
        {keeping ? 'Keeping…' : errored ? 'Retry' : 'Keep'}
      </button>
    </div>
  );
}
